import React, { useEffect, useRef } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Stack,
  Paper,
  Avatar,
  useMediaQuery,
} from "@mui/material";
import Grid from '@mui/material/Grid'; // Normal Grid import
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import SimpleYouTubePreview from "../components/SimpleYouTubePreview";

// Icons
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import NatureIcon from '@mui/icons-material/Nature';
import RecyclingIcon from '@mui/icons-material/Recycling';
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SpaIcon from '@mui/icons-material/Spa';
// Components
import HomeSlider from "../components/Home/HomeSlider";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};
const cleanupYouTubeMessages = () => {
  try {
    // Global YouTube iframe API mesajlarını temizle
    const existingPlayers = document.querySelectorAll(
      'iframe[src*="youtube.com"]'
    );
    existingPlayers.forEach((iframe) => {
      try {
        // iframe'in kaynağını değiştirip API bağlantısını kes
        if (iframe && iframe.contentWindow) {
          iframe.src = "about:blank";
        }
      } catch (e) {
        console.warn("YouTube iframe temizleme hatası:", e);
      }
    });
  } catch (error) {
    console.warn("YouTube mesajları temizlenirken hata:", error);
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const HomePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Refs for scrolling
  const productsRef = useRef(null);
  const contactRef = useRef(null);

  // Scroll handler
  const scrollToSection = (elementRef) => {
    if (elementRef && elementRef.current) {
      window.scrollTo({
        top: elementRef.current.offsetTop - 80, // Adjust for header
        behavior: "smooth",
      });
    }
  };

  // useEffect güncellemesi:
  useEffect(() => {
    document.title =
      "SGT Global Trade | " + (i18n.language === "tr" ? "Ana Sayfa" : "Home");

    // Temizleme fonksiyonu
    return () => {
      try {
        cleanupYouTubeMessages();
      } catch (error) {
        console.error("Temizleme hatası:", error);
      }
    };
  }, [i18n.language]);

  // Handle navigation with scroll
  const handleNavigation = (path, ref = null) => {
    if (path === "/products" && ref) {
      scrollToSection(ref);
    } else if (path === "/contact" && contactRef) {
      navigate(path);
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    } else {
      navigate(path);
    }
  };

  // Product data with specific names
  const productItems = [
    {
      id: "barley",
      name: { tr: "Arpa", en: "Barley" },
      image: "/images/barley.jpeg",
    },
    {
      id: "wheat",
      name: { tr: "Buğday", en: "Wheat" },
      image: "/images/wheat.jpeg",
    },
    {
      id: "corn",
      name: { tr: "Mısır", en: "Corn" },
      image: "/images/corn.jpeg",
    },
    {
      id: "corn-oil",
      name: { tr: "Mısır Yağı", en: "Corn Oil" },
      image: "/images/corn-oil.jpeg",
    },
    {
      id: "sunflower-oil",
      name: { tr: "Ayçiçek Yağı", en: "Sunflower Oil" },
      image: "/images/sunflower-oil.jpeg",
    },
    {
      id: "chicken",
      name: { tr: "Tavuk", en: "Chicken" },
      image: "/images/chicken.jpeg",
    },
  ];

  return (
    <Box
      sx={{
        pb: 0,
        position: "relative",
        // Sayfa içeriğini sıfırdan başlat (header altından değil)
        mt: 0,
        pt: 0,
      }}
    >
      {/* Hero Slider - Full screen ve header ile birleştirilmiş */}
      <HomeSlider onViewProducts={() => scrollToSection(productsRef)} />

      {/* Sürdürülebilir Gelecek Bölümü - IMAGE OLMADAN RESPONSIVE MODERNIZE EDİLMİŞ */}
      <Box
        component="section"
        sx={{
          py: { xs: 5, sm: 6, md: 8 },
          mt: { xs: 3, md: 6 },
          backgroundColor: theme.palette.background.paper,
          borderRadius: { md: "24px" },
          maxWidth: { md: "94%", lg: "90%" },
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 }, // Sol ve sağ paddingi artırarak sola kayma sorununu çözüyoruz
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "200px",
            height: "200px",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            opacity: 0.1,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
          {" "}
          {/* Container padding'i azaltarak içeriğin ekran dışına çıkmasını engelliyoruz */}
          <Grid
            container
            spacing={{ xs: 2, sm: 3, md: 4 }}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item xs={12} sm={10} md={10} lg={8}>
              {" "}
              {/* Genişliği arttırıp merkeze konumlandırıyoruz */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                custom={0}
              >
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    fontSize: { xs: "1.7rem", sm: "2rem", md: "2.75rem" },
                    lineHeight: 1.2,
                    mb: { xs: 2, md: 3 },
                    textAlign: "center", // Tüm ekran boyutlarında ortalıyoruz
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    width: "100%", // Taşmayı önlemek için genişliği sabitle
                  }}
                >
                  {i18n.language === "tr"
                    ? "Sürdürülebilir Bir Gelecek Arzuluyoruz"
                    : "We Aspire for a Sustainable Future"}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: { xs: 2, md: 3 },
                    color: theme.palette.text.primary,
                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                    lineHeight: 1.7,
                    textAlign: "center", // Tüm ekran boyutlarında ortalıyoruz
                    px: { xs: 1, sm: 2, md: 4 }, // İçeriği kenarlara yaklaştırmamak için padding
                  }}
                >
                  {i18n.language === "tr"
                    ? "SGT Global Trade olarak, dünya kaynaklarını korumak ve gelecek nesillere daha yaşanabilir bir gezegen bırakmak amacıyla sürdürülebilir ticaret pratiklerini benimsiyoruz. Tüm tedarik zincirimizde çevresel etkileri azaltmak için yenilikçi çözümler geliştiriyor, doğal kaynakları verimli kullanıyor ve ekolojik dengeyi koruyoruz."
                    : "At SGT Global Trade, we embrace sustainable trade practices with the aim of preserving global resources and leaving a more habitable planet for future generations. We develop innovative solutions to reduce environmental impacts throughout our supply chain, use natural resources efficiently, and protect ecological balance."}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: { xs: 3, md: 4 },
                    color: theme.palette.text.primary,
                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                    lineHeight: 1.7,
                    display: { xs: "none", sm: "block" }, // Mobilde gizle, tablet ve üzerinde göster
                    textAlign: "center", // Tüm ekran boyutlarında ortalıyoruz
                    px: { xs: 1, sm: 2, md: 4 }, // İçeriği kenarlara yaklaştırmamak için padding
                  }}
                >
                  {i18n.language === "tr"
                    ? "Yenilenebilir enerji kaynaklarının kullanımını destekliyor, karbon ayak izimizi minimize ediyor ve üreticileri sürdürülebilir tarım yöntemlerini benimsemeye teşvik ediyoruz. Sorumlu ticaret anlayışımız, ekonomik büyümeyi çevresel sürdürülebilirlik ile dengeleyerek, hem bugünün hem de yarının ihtiyaçlarını karşılamayı hedefliyor."
                    : "We support the use of renewable energy sources, minimize our carbon footprint, and encourage producers to adopt sustainable agricultural methods. Our responsible trade approach aims to meet the needs of both today and tomorrow by balancing economic growth with environmental sustainability."}
                </Typography>

                {/* Responsive Sürdürülebilirlik Özellikleri Grid */}
                <Grid
                  container
                  spacing={{ xs: 2, sm: 3, md: 4 }}
                  justifyContent="center"
                >
                  {/* Mobilde 2x2 grid, diğer ekranlarda 4 yan yana */}
                  <Grid item xs={6} sm={3}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column", // Her ekran boyutunda dikey düzen
                        justifyContent: "center",
                        textAlign: "center",
                        p: { xs: 1, sm: 2 },
                        borderRadius: "16px",
                        transition: "all 0.3s ease",
                        height: "100%", // Tüm kutuların eşit yükseklikte olmasını sağlar
                        "&:hover": {
                          backgroundColor: `${theme.palette.primary.light}15`,
                          transform: "translateY(-5px)",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: `${theme.palette.primary.light}30`,
                          color: theme.palette.primary.main,
                          mb: 1.5,
                          width: { xs: 48, sm: 56, md: 64 },
                          height: { xs: 48, sm: 56, md: 64 },
                          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <NatureIcon
                          sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }}
                        />
                      </Avatar>
                      <Typography
                        variant="h6"
                        component="p"
                        fontWeight={600}
                        fontSize={{
                          xs: "0.85rem",
                          sm: "0.95rem",
                          md: "1.1rem",
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Ekolojik Denge"
                          : "Ecological Balance"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column", // Her ekran boyutunda dikey düzen
                        justifyContent: "center",
                        textAlign: "center",
                        p: { xs: 1, sm: 2 },
                        borderRadius: "16px",
                        transition: "all 0.3s ease",
                        height: "100%", // Tüm kutuların eşit yükseklikte olmasını sağlar
                        "&:hover": {
                          backgroundColor: `${theme.palette.primary.light}15`,
                          transform: "translateY(-5px)",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: `${theme.palette.primary.light}30`,
                          color: theme.palette.primary.main,
                          mb: 1.5,
                          width: { xs: 48, sm: 56, md: 64 },
                          height: { xs: 48, sm: 56, md: 64 },
                          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <AutorenewIcon
                          sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }}
                        />
                      </Avatar>
                      <Typography
                        variant="h6"
                        component="p"
                        fontWeight={600}
                        fontSize={{
                          xs: "0.85rem",
                          sm: "0.95rem",
                          md: "1.1rem",
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Sorumlu Ticaret"
                          : "Responsible Trade"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column", // Her ekran boyutunda dikey düzen
                        justifyContent: "center",
                        textAlign: "center",
                        p: { xs: 1, sm: 2 },
                        borderRadius: "16px",
                        transition: "all 0.3s ease",
                        height: "100%", // Tüm kutuların eşit yükseklikte olmasını sağlar
                        "&:hover": {
                          backgroundColor: `${theme.palette.primary.light}15`,
                          transform: "translateY(-5px)",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: `${theme.palette.primary.light}30`,
                          color: theme.palette.primary.main,
                          mb: 1.5,
                          width: { xs: 48, sm: 56, md: 64 },
                          height: { xs: 48, sm: 56, md: 64 },
                          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <RecyclingIcon
                          sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }}
                        />
                      </Avatar>
                      <Typography
                        variant="h6"
                        component="p"
                        fontWeight={600}
                        fontSize={{
                          xs: "0.85rem",
                          sm: "0.95rem",
                          md: "1.1rem",
                        }}
                      >
                        {i18n.language === "tr" ? "Geri Dönüşüm" : "Recycling"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column", // Her ekran boyutunda dikey düzen
                        justifyContent: "center",
                        textAlign: "center",
                        p: { xs: 1, sm: 2 },
                        borderRadius: "16px",
                        transition: "all 0.3s ease",
                        height: "100%", // Tüm kutuların eşit yükseklikte olmasını sağlar
                        "&:hover": {
                          backgroundColor: `${theme.palette.primary.light}15`,
                          transform: "translateY(-5px)",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: `${theme.palette.primary.light}30`,
                          color: theme.palette.primary.main,
                          mb: 1.5,
                          width: { xs: 48, sm: 56, md: 64 },
                          height: { xs: 48, sm: 56, md: 64 },
                          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <SpaIcon
                          sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }}
                        />
                      </Avatar>
                      <Typography
                        variant="h6"
                        component="p"
                        fontWeight={600}
                        fontSize={{
                          xs: "0.85rem",
                          sm: "0.95rem",
                          md: "1.1rem",
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Doğal Kaynaklar"
                          : "Natural Resources"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Ürünler Bölümü */}
      <Box
        component="section"
        ref={productsRef}
        sx={{
          py: { xs: 5, md: 10 },
          mt: { xs: 4, md: 6 },
          backgroundImage: theme.palette.background.wheatOverlay,
          position: "relative",
          zIndex: 1,
          scrollMarginTop: "80px", // For smooth scrolling
        }}
      >
        <Container maxWidth="xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            custom={0}
          >
            <Stack
              direction="column"
              spacing={1}
              alignItems="center"
              sx={{ mb: { xs: 5, md: 7 } }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  mb: 1,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                }}
              >
                {i18n.language === "tr" ? "Ürünlerimiz" : "Our Products"}
              </Box>

              <Typography
                variant="h2"
                component="h2"
                align="center"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  lineHeight: 1.2,
                }}
              >
                {i18n.language === "tr"
                  ? "Kalite ve Güven Odaklı"
                  : "Quality and Trust Focused"}
              </Typography>

              <Divider
                sx={{
                  width: "80px",
                  height: "4px",
                  backgroundColor: theme.palette.secondary.main,
                  my: 2,
                  borderRadius: "2px",
                }}
              />

              <Typography
                variant="h5"
                component="p"
                align="center"
                sx={{
                  fontWeight: 400,
                  color: theme.palette.text.primary,
                  maxWidth: "800px",
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                {i18n.language === "tr"
                  ? "Yüksek kaliteli tarım ürünleri ve bitkisel yağlar ile global pazarda güvenilir tedarikçiniz"
                  : "Your reliable supplier in the global market with high quality agricultural products and vegetable oils"}
              </Typography>
            </Stack>
          </motion.div>

          {/* Ürün Kartları - Modernleştirilmiş Grid Yapısı */}
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            <Grid
              container
              spacing={{ xs: 2, sm: 3, md: 4 }}
              justifyContent="center"
              sx={{ my: 4 }}
            >
              {productItems.map((product, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={product.id || index}
                  sx={{ display: "flex" }}
                >
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeInUp}
                    custom={index + 1}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "16px",
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        border: "1px solid rgba(0, 0, 0, 0.08)",
                        backgroundColor: "white",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 16px 30px rgba(0, 0, 0, 0.12)",
                          "& .MuiCardMedia-root": {
                            transform: "scale(1.05)",
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          overflow: "hidden",
                          position: "relative",
                          height: 0,
                          paddingTop: "56.25%", // 16:9 aspect ratio (daha yatay)
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={product.image || "/images/placeholder.jpg"}
                          alt={
                            i18n.language === "tr"
                              ? product.name.tr
                              : product.name.en
                          }
                          sx={{
                            transition: "transform 0.6s ease",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                      <CardContent
                        sx={{
                          p: { xs: 2, md: 2.5 },
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="h5"
                          component="h3"
                          gutterBottom
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            fontSize: { xs: "1.125rem", md: "1.35rem" },
                            minHeight: { sm: "48px" },
                            display: "flex",
                            alignItems: "flex-start",
                          }}
                        >
                          {i18n.language === "tr"
                            ? product.name.tr
                            : product.name.en}
                        </Typography>

                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            flexGrow: 1,
                            fontSize: { xs: "0.875rem", md: "0.95rem" },
                            lineHeight: 1.5,
                            minHeight: { sm: "60px" },
                          }}
                        >
                          {i18n.language === "tr"
                            ? `Yüksek kaliteli ${product.name.tr} ürünlerimiz hakkında detaylı bilgi için tıklayın.`
                            : `Click for detailed information about our high-quality ${product.name.en} products.`}
                        </Typography>

                        <Button
                          variant="outlined"
                          color="primary"
                          endIcon={<ArrowForwardIcon />}
                          onClick={() =>
                            navigate(`/products/${product.id || ""}`)
                          }
                          sx={{
                            alignSelf: "flex-start",
                            mt: "auto",
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            px: 2.5,
                            py: 1.25,
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main,
                              color: "white",
                            },
                          }}
                        >
                          {i18n.language === "tr" ? "Detaylar" : "Details"}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Container>
      </Box>

      {/* Tanıtım Filmleri Bölümü - Düzeltilmiş yerleşim */}
      {/* Tanıtım Filmleri Bölümü - Düzeltilmiş yerleşim */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "#f8fafc",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundSize: "400px",
            opacity: 0.05,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            custom={0}
          >
            <Stack
              direction="column"
              spacing={1}
              alignItems="center"
              sx={{ mb: { xs: 6, md: 8 } }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  mb: 1,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                }}
              >
                {i18n.language === "tr"
                  ? "Tanıtım Filmlerimiz"
                  : "Our Promo Videos"}
              </Box>

              <Typography
                variant="h2"
                component="h2"
                align="center"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  lineHeight: 1.2,
                }}
              >
                {i18n.language === "tr"
                  ? "Dünya Ticaretinde Güvenilir Ortağınız"
                  : "Your Reliable Partner in Global Trade"}
              </Typography>

              <Divider
                sx={{
                  width: "80px",
                  height: "4px",
                  backgroundColor: theme.palette.secondary.main,
                  my: 2,
                  borderRadius: "2px",
                }}
              />

              <Typography
                variant="h5"
                component="p"
                align="center"
                sx={{
                  fontWeight: 400,
                  color: theme.palette.text.secondary,
                  maxWidth: "800px",
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                {i18n.language === "tr"
                  ? "SGT Dış Ticaret'i daha yakından tanımak için tanıtım filmlerimizi izleyin"
                  : "Watch our promotional videos to get to know SGT Global Trade more closely"}
              </Typography>
            </Stack>
          </motion.div>

          {/* Responsive düzenleme - Mobil ve tablet görünümünde videoların alt alta ortalı görünmesi */}
          <Container
            maxWidth="lg"
            sx={{
              px: { xs: 2, sm: 4 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Grid
              container
              spacing={4}
              sx={{
                justifyContent: { xs: "center", md: "space-between" },
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              {/* Türkçe Video */}
              <Grid
                sx={{
                  gridColumn: { xs: "span 12", sm: "span 10", md: "span 3" },
                  width: { xs: "100%", sm: "80%", md: "25%" },
                  mx: "auto",
                }}
              >
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                  custom={1}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "16px",
                      overflow: "hidden",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    {/* Burada SimpleYouTubePreview kullanıyoruz */}
                    <SimpleYouTubePreview
                      videoId="hqOVZPNrUYE"
                      title="SGT Global Trade - Türkçe Tanıtım"
                    />
                    <CardContent
                      sx={{
                        p: 3,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Türkçe
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "SGT Dış Ticaret Türkçe tanıtım filmi"
                          : "SGT Global Trade Turkish promotional video"}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              {/* İngilizce Video */}
              <Grid
                sx={{
                  gridColumn: { xs: "span 12", sm: "span 10", md: "span 3" },
                  width: { xs: "100%", sm: "80%", md: "25%" },
                  mx: "auto",
                }}
              >
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                  custom={2}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "16px",
                      overflow: "hidden",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    {/* SimpleYouTubePreview kullanıyoruz */}
                    <SimpleYouTubePreview
                      videoId="pO6lPS8IEDo"
                      title="SGT Global Trade - English Promo"
                    />
                    <CardContent
                      sx={{
                        p: 3,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        English
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "SGT Dış Ticaret İngilizce tanıtım filmi"
                          : "SGT Global Trade English promotional video"}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              {/* Arapça Video */}
              <Grid
                sx={{
                  gridColumn: { xs: "span 12", sm: "span 10", md: "span 3" },
                  width: { xs: "100%", sm: "80%", md: "25%" },
                  mx: "auto",
                }}
              >
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                  custom={3}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "16px",
                      overflow: "hidden",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    {/* SimpleYouTubePreview kullanıyoruz */}
                    <SimpleYouTubePreview
                      videoId="0L4KywONQgg"
                      title="SGT Global Trade - Arabic Promo"
                    />
                    <CardContent
                      sx={{
                        p: 3,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        العربية
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "SGT Dış Ticaret Arapça tanıtım filmi"
                          : "SGT Global Trade Arabic promotional video"}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              {/* Rusça Video */}
              <Grid
                sx={{
                  gridColumn: { xs: "span 12", sm: "span 10", md: "span 3" },
                  width: { xs: "100%", sm: "80%", md: "25%" },
                  mx: "auto",
                }}
              >
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                  custom={4}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "16px",
                      overflow: "hidden",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    {/* SimpleYouTubePreview kullanıyoruz */}
                    <SimpleYouTubePreview
                      videoId="1iPqJWqeTuc"
                      title="SGT Global Trade - Russian Promo"
                    />
                    <CardContent
                      sx={{
                        p: 3,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Русский
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "SGT Dış Ticaret Rusça tanıtım filmi"
                          : "SGT Global Trade Russian promotional video"}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Container>
      </Box>

      {/* Sayılarla Biz Bölümü - MOBILE OPTIMIZED */}
      <Box
        component="section"
        sx={{
          py: { xs: 6, md: 10 },
          backgroundColor: theme.palette.background.default,
          backgroundImage: `linear-gradient(rgba(255,255,255,0.3), rgba(255,255,255,0.5)), url('/images/basarilarimiz.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Container maxWidth="xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            custom={0}
          >
            <Stack
              direction="column"
              spacing={1}
              alignItems="center"
              sx={{ mb: { xs: 4, md: 8 } }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  mb: 1,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                }}
              >
                {i18n.language === "tr" ? "Sayılarla Biz" : "Our Numbers"}
              </Box>

              <Typography
                variant="h2"
                component="h2"
                align="center"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: { xs: "2rem", md: "3rem" },
                  lineHeight: 1.2,
                }}
              >
                {i18n.language === "tr" ? "Başarılarımız" : "Our Achievements"}
              </Typography>

              <Divider
                sx={{
                  width: "80px",
                  height: "4px",
                  backgroundColor: theme.palette.secondary.main,
                  my: 2,
                  borderRadius: "2px",
                }}
              />

              <Typography
                variant="h5"
                component="p"
                align="center"
                sx={{
                  fontWeight: 400,
                  color: theme.palette.text.primary,
                  maxWidth: "800px",
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                {i18n.language === "tr"
                  ? "Global ticaret alanında elde ettiğimiz başarılar"
                  : "Our achievements in global trade"}
              </Typography>
            </Stack>
          </motion.div>
                   <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
            {/* Teklif Sayısı */}
            <Grid item xs={12} md={3}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                custom={1}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 4 },
                    height: "100%",
                    textAlign: "center",
                    backgroundColor: "white",
                    borderRadius: 4,
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: "0 16px 32px rgba(0, 0, 0, 0.1)",
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      "& .number": {
                        color: "white",
                      },
                      "& .title": {
                        color: "white",
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    component="p"
                    className="number"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "2rem", md: "3.5rem" },
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    337
                  </Typography>
                  <Typography
                    variant="h6"
                    component="p"
                    className="title"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: { xs: "0.875rem", md: "1.25rem" },
                    }}
                  >
                    {i18n.language === "tr" ? "TEKLİF" : "QUOTES"}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>

            {/* Sipariş Sayısı */}
            <Grid item xs={6} md={3}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                custom={2}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 4 },
                    height: "100%",
                    textAlign: "center",
                    backgroundColor: "white",
                    borderRadius: 4,
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: "0 16px 32px rgba(0, 0, 0, 0.1)",
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      "& .number": {
                        color: "white",
                      },
                      "& .title": {
                        color: "white",
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    component="p"
                    className="number"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "2rem", md: "3.5rem" },
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    267
                  </Typography>
                  <Typography
                    variant="h6"
                    component="p"
                    className="title"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: { xs: "0.875rem", md: "1.25rem" },
                    }}
                  >
                    {i18n.language === "tr" ? "SİPARİŞ" : "ORDERS"}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>

            {/* Sevkiyat Sayısı */}
            <Grid item xs={6} md={3}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                custom={3}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 4 },
                    height: "100%",
                    textAlign: "center",
                    backgroundColor: "white",
                    borderRadius: 4,
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: "0 16px 32px rgba(0, 0, 0, 0.1)",
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      "& .number": {
                        color: "white",
                      },
                      "& .title": {
                        color: "white",
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    component="p"
                    className="number"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "2rem", md: "3.5rem" },
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    264
                  </Typography>
                  <Typography
                    variant="h6"
                    component="p"
                    className="title"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: { xs: "0.875rem", md: "1.25rem" },
                    }}
                  >
                    {i18n.language === "tr" ? "SEVKİYAT" : "SHIPMENTS"}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>

            {/* Mutlu Müşteri Sayısı */}
            <Grid item xs={6} md={3}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                custom={4}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 4 },
                    height: "100%",
                    textAlign: "center",
                    backgroundColor: "white",
                    borderRadius: 4,
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: "0 16px 32px rgba(0, 0, 0, 0.1)",
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      "& .number": {
                        color: "white",
                      },
                      "& .title": {
                        color: "white",
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    component="p"
                    className="number"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "2rem", md: "3.5rem" },
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    255
                  </Typography>
                  <Typography
                    variant="h6"
                    component="p"
                    className="title"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: { xs: "0.875rem", md: "1.25rem" },
                    }}
                  >
                    {i18n.language === "tr" ? "MUTLU MÜŞTERİ" : "HAPPY CLIENTS"}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
          
        </Container>
      </Box>

      {/* Teklif Al CTA Bölümü */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            custom={0}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 8 },
                borderRadius: "24px",
                overflow: "hidden",
                position: "relative",
                border: "1px solid rgba(0, 0, 0, 0.05)",
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                color: "white",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "300px",
                  height: "300px",
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.1,
                  zIndex: 0,
                },
              }}
            >
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  align="center"
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    fontSize: { xs: "2rem", md: "2.75rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {i18n.language === "tr"
                    ? "Hemen Teklif Alın"
                    : "Get a Quote Now"}
                </Typography>

                <Typography
                  variant="h6"
                  component="p"
                  align="center"
                  sx={{
                    mb: 6,
                    fontWeight: 400,
                    maxWidth: "800px",
                    mx: "auto",
                    opacity: 0.9,
                    fontSize: { xs: "1rem", md: "1.25rem" },
                    lineHeight: 1.6,
                  }}
                >
                  {i18n.language === "tr"
                    ? "Ürünlerimiz hakkında detaylı bilgi ve fiyat teklifi almak için formu doldurun, uzman ekibimiz en kısa sürede sizinle iletişime geçsin."
                    : "Fill out the form to get detailed information and price quotes about our products, and our expert team will contact you as soon as possible."}
                </Typography>

                <Box sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={() => navigate("/quote")}
                    sx={{
                      px: 6,
                      py: 2,
                      borderRadius: "12px",
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      boxShadow: "0 8px 16px rgba(0, 0, 0, 0.15)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 12px 24px rgba(0, 0, 0, 0.25)",
                      },
                    }}
                  >
                    {i18n.language === "tr" ? "Teklif Al" : "Get a Quote"}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* İletişim Bilgileri - MODERNIZE EDILMIS */}
      {/* İletişim Bilgileri - Yeniden Tasarlanmış ve Sadeleştirilmiş */}
      <Box
        component="section"
        ref={contactRef}
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "#f8fafc",
          position: "relative",
          overflow: "hidden",
          scrollMarginTop: "80px", // For smooth scrolling
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundSize: "cover",
            opacity: 0.05,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            custom={0}
          >
            <Stack
              direction="column"
              spacing={1}
              alignItems="center"
              sx={{ mb: { xs: 6, md: 8 } }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  mb: 1,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                }}
              >
                {i18n.language === "tr" ? "İletişim" : "Contact Us"}
              </Box>

              <Typography
                variant="h2"
                component="h2"
                align="center"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  lineHeight: 1.2,
                }}
              >
                {i18n.language === "tr"
                  ? "Bizimle İletişime Geçin"
                  : "Get in Touch With Us"}
              </Typography>

              <Divider
                sx={{
                  width: "80px",
                  height: "4px",
                  backgroundColor: theme.palette.secondary.main,
                  my: 2,
                  borderRadius: "2px",
                }}
              />

              <Typography
                variant="h5"
                component="p"
                align="center"
                sx={{
                  fontWeight: 400,
                  color: theme.palette.text.secondary,
                  maxWidth: "800px",
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                {i18n.language === "tr"
                  ? "Sorularınız için bizimle iletişime geçin, size yardımcı olmaktan memnuniyet duyarız"
                  : "Contact us for your questions, we are happy to help you"}
              </Typography>
            </Stack>
          </motion.div>

          {/* İletişim Kartları - YAN YANA MODERN TASARIM */}
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              maxWidth: "1200px",
              mx: "auto",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: "24px",
                backgroundColor: "white",
                border: "1px solid rgba(0, 0, 0, 0.05)",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.07)",
                backgroundImage:
                  "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -100,
                  left: -100,
                  width: "300px",
                  height: "300px",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}30)`,
                  zIndex: 0,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -100,
                  right: -100,
                  width: "300px",
                  height: "300px",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${theme.palette.secondary.light}30, ${theme.palette.primary.light}20)`,
                  zIndex: 0,
                },
              }}
            >
              <Grid
                container
                spacing={4}
                justifyContent="center"
                alignItems="center"
              >
                {/* Telefon */}
                <Grid sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeInUp}
                    custom={1}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                        justifyContent: { xs: "center", sm: "flex-start" },
                        textAlign: "center",
                        p: 3,
                        borderRadius: "20px",
                        background: "rgba(255, 255, 255, 0.5)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.8)",
                        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                        transition: "all 0.4s ease",
                        height: "100%",
                        "&:hover": {
                          transform: "translateY(-10px)",
                          boxShadow: "0 15px 35px rgba(0, 0, 0, 0.1)",
                          "& .contact-icon": {
                            transform: "scale(1.1) rotate(5deg)",
                          },
                        },
                      }}
                    >
                      <Avatar
                        className="contact-icon"
                        sx={{
                          width: { xs: 90, md: 100 },
                          height: { xs: 90, md: 100 },
                          mb: { xs: 3, sm: 0 },
                          mr: { xs: 0, sm: 4 },
                          backgroundColor: `${theme.palette.primary.main}15`,
                          color: theme.palette.primary.main,
                          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                          border: `2px solid ${theme.palette.primary.light}`,
                          transition: "transform 0.4s ease",
                        }}
                      >
                        <LocalPhoneOutlinedIcon sx={{ fontSize: 44 }} />
                      </Avatar>

                      <Box>
                        <Typography
                          variant="h4"
                          component="h3"
                          sx={{
                            mb: 2,
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontSize: { xs: "1.5rem", md: "1.75rem" },
                          }}
                        >
                          {i18n.language === "tr" ? "Bizi Arayın" : "Call Us"}
                        </Typography>

                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1.5,
                            fontSize: { xs: "1.25rem", md: "1.5rem" },
                            fontWeight: 600,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            letterSpacing: "0.5px",
                          }}
                        >
                          +90 533 399 2188
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: { xs: "1rem", md: "1.1rem" },
                          }}
                        >
                          {i18n.language === "tr"
                            ? "09:00 - 18:00 (Haftanın 7 günü)"
                            : "09:00 - 18:00 (7 days a week)"}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>

                {/* Email */}
                <Grid sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeInUp}
                    custom={2}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                        justifyContent: { xs: "center", sm: "flex-start" },
                        textAlign: "center",
                        p: 3,
                        borderRadius: "20px",
                        background: "rgba(255, 255, 255, 0.5)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.8)",
                        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                        transition: "all 0.4s ease",
                        height: "100%",
                        "&:hover": {
                          transform: "translateY(-10px)",
                          boxShadow: "0 15px 35px rgba(0, 0, 0, 0.1)",
                          "& .contact-icon": {
                            transform: "scale(1.1) rotate(-5deg)",
                          },
                        },
                      }}
                    >
                      <Avatar
                        className="contact-icon"
                        sx={{
                          width: { xs: 90, md: 100 },
                          height: { xs: 90, md: 100 },
                          mb: { xs: 3, sm: 0 },
                          mr: { xs: 0, sm: 4 },
                          backgroundColor: `${theme.palette.secondary.main}15`,
                          color: theme.palette.secondary.main,
                          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                          border: `2px solid ${theme.palette.secondary.light}`,
                          transition: "transform 0.4s ease",
                        }}
                      >
                        <EmailOutlinedIcon sx={{ fontSize: 44 }} />
                      </Avatar>

                      <Box>
                        <Typography
                          variant="h4"
                          component="h3"
                          sx={{
                            mb: 2,
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontSize: { xs: "1.5rem", md: "1.75rem" },
                          }}
                        >
                          {i18n.language === "tr"
                            ? "E-posta Gönderin"
                            : "Email Us"}
                        </Typography>

                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1.5,
                            fontSize: { xs: "1.25rem", md: "1.5rem" },
                            fontWeight: 600,
                            background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            letterSpacing: "0.5px",
                          }}
                        >
                          info@sgttr.com
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: { xs: "1rem", md: "1.1rem" },
                          }}
                        >
                          {i18n.language === "tr"
                            ? "E-postalarınıza hızlı yanıt veriyoruz"
                            : "We respond to emails quickly"}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          <Box sx={{ textAlign: "center", mt: 8 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => {
                // Doğrudan contact sayfasına git, scroll reset olmadan
                navigate("/contact", { replace: true });
                // veya daha basit bir çözüm için:
                // window.location.href = "/contact";
              }}
              sx={{
                px: 5,
                py: 1.5,
                borderRadius: "8px",
                fontWeight: 600,
                boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 12px 20px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              {i18n.language === "tr" ? "Bize Ulaşın" : "Contact Us"}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;